import React, { useCallback, useEffect } from 'react';
import { Button, Col, Form, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { FormProvider, useForm } from 'react-hook-form';
import FormInput from '@/components/forms/FormInput';
import {
  GameType,
  PokerVariant,
  TournamentInfoDto,
  TournamentStatus,
  TournamentUpdateDto,
  TournamentUpdateInput,
} from '@repo/common-types';
import { ReactComponent as PeopleIcon } from '@/assets/icons/people.svg';
import { ReactComponent as TetherIcon } from '@/assets/tokens/tether-green.svg';
import usePayoutStructures from '@/hooks/usePayoutStructures';
import useBlindStructures from '@/hooks/useBlindStructures';
import checkVariants from '@/common/forms/table-handling/checkVariants';
import * as yup from 'yup';

import useGetPlayerTiers from '@/hooks/useGetPlayerTiers';
import { yupResolver } from '@hookform/resolvers/yup';

// Local enum constants for tournament customization
const TournamentIcon = {
  NONE: 'NONE',
  PHNM_LOGO: 'PHNM_LOGO',
  STAR_LOGO: 'STAR_LOGO',
} as const;

const TournamentColor = {
  DEFAULT_WHITE: 'DEFAULT_WHITE',
  PURPLE: 'PURPLE',
  PINK: 'PINK',
  LIGHT_BLUE: 'LIGHT_BLUE',
  LIGHT_GREEN: 'LIGHT_GREEN',
  ORANGE: 'ORANGE',
} as const;

const getValidationSchema = ({ blindStructures, payoutStructures }) => {
  return yup.object({
    name: yup.string().required('Name is required').min(1, 'Name is required'),
    game: yup.string().required('Game is required'),
    buyIn: yup.number().required('Buy in is required').min(0, 'Buy in must be greater than 0'),
    maxPlayers: yup.number().required('Max players is required').min(1, 'Max players must be greater than 1'),
    rake: yup.number().min(0, 'Rake must be greater than 0'),
    payoutStructureId: yup
      .string()
      .required('Payout structure is required')
      .test(
        'payout-structure-must-handle-max-players',
        'Payout structure must have enough payouts to handle the player count',
        (value, context) => {
          const selectedPayoutStructure = payoutStructures?.find((structure) => structure.id === value);
          const largestPayoutBracket = selectedPayoutStructure?.brackets?.reduce((acc, curr) => {
            if (curr.playersTo > acc) {
              acc = curr.playersTo;
            }
            return acc;
          }, 0);

          if (largestPayoutBracket && largestPayoutBracket < context?.parent?.maxPlayers) {
            return false;
          }

          return true;
        },
      ),
    tableSize: yup.number().required('Table size is required').min(1, 'Table size must be greater than 1'),
    startingChips: yup.number().required('Starting chips is required').min(0, 'Starting chips must be greater than 0'),
    blindIntervalInMinutes: yup
      .number()
      .required('Blind interval is required')
      .min(0, 'Blind interval must be greater than 0'),
    blindStructureId: yup
      .string()
      .required('Blind structure is required')
      .test(
        'blind-structure-has-bring-ins-if-stud-or-mixed',
        'Stud and mixed games require a blind structure with bring-ins',
        (value, context) => {
          try {
            if (context?.parent?.game && value) {
              const currentBlindStructure = blindStructures?.find((structure) => structure.id === value);
              const { stud, mixed } = checkVariants([context?.parent?.game as PokerVariant]);
              if (stud || mixed) {
                const validBlindStructureForVariant = currentBlindStructure?.levels?.every(
                  (level) => !!level.bringIn && level.bringIn > 0,
                );
                if (!validBlindStructureForVariant) {
                  return false;
                }
              }
            }
            return true;
          } catch (error) {
            console.error('[blind-structure-has-bring-ins-if-stud-or-mixed]', error);
            return false;
          }
        },
      ),
    minimumTierRequired: yup.string().nullable().optional(),
    // Timebank validation fields
    startingTimebankInSeconds: yup
      .number()
      .required('Starting timebank is required')
      .min(10, 'Starting timebank must be at least 10 seconds')
      .max(300, 'Starting timebank cannot exceed 300 seconds (5 minutes)'),
    timebankRefillAmountInSeconds: yup
      .number()
      .required('Timebank refill amount is required')
      .min(10, 'Timebank refill amount must be at least 10 seconds')
      .max(120, 'Timebank refill amount cannot exceed 120 seconds'),
    timebankRefillEveryLevels: yup
      .number()
      .required('Timebank refill frequency is required')
      .min(0, 'Timebank refill frequency must be 0 or greater (0 = disabled)')
      .max(50, 'Timebank refill frequency cannot exceed 50 levels'),
    startingDisconnectionTimebankInSeconds: yup
      .number()
      .required('Starting disconnection timebank is required')
      .min(10, 'Starting disconnection timebank must be at least 10 seconds')
      .max(300, 'Starting disconnection timebank cannot exceed 300 seconds (5 minutes)'),
  });
};

const EditSngModalForm = ({
  onClose,
  selectedTournament,
  onUpdate,
  formStatus,
}: {
  onClose: () => void;
  onUpdate: (data: Partial<TournamentUpdateDto>) => void;
  selectedTournament: TournamentInfoDto;
  formStatus: {
    isLoading: boolean;
  };
}) => {
  const { playerTiers } = useGetPlayerTiers();
  const { payoutStructures } = usePayoutStructures();
  const { blindStructures } = useBlindStructures();

  const form = useForm({
    resolver: yupResolver(
      getValidationSchema({
        blindStructures: [...(blindStructures ?? []), selectedTournament?.blindStructure],
        payoutStructures: [...(payoutStructures ?? []), selectedTournament?.payoutStructure],
      }),
    ),
    mode: 'onChange',
  });

  const {
    handleSubmit,
    formState: { dirtyFields, isDirty, errors },
    control,
    watch,
    getValues,
  } = form;

  useEffect(() => {
    if (selectedTournament) {
      form.reset(selectedTournament);
    }
  }, [selectedTournament, form]);

  // State for the admin interface (total buy-in and rake amount)
  const [adminTotalBuyIn, setAdminTotalBuyIn] = React.useState(0);
  const [adminRakeAmount, setAdminRakeAmount] = React.useState(0);

  // Initialize admin values from existing tournament
  useEffect(() => {
    if (selectedTournament) {
      setAdminTotalBuyIn(selectedTournament.totalBuyin);
      setAdminRakeAmount(selectedTournament?.fee);
    }
  }, [selectedTournament]);

  const maxPlayers = watch('maxPlayers');
  useEffect(() => {
    if (Object.keys(form.formState.dirtyFields).includes('payoutStructureId')) {
      form.trigger('payoutStructureId');
    }
  }, [maxPlayers, form.formState.dirtyFields]);

  const game = watch('game');
  useEffect(() => {
    if (Object.keys(form.formState.dirtyFields).includes('blindStructureId')) {
      form.trigger('blindStructureId');
    }
  }, [game, form.formState.dirtyFields]);

  // Check if tournament is in a state where most fields should be disabled
  const tournamentStatus = selectedTournament?.status;
  const restrictedStatuses: TournamentStatus[] = [
    TournamentStatus.RUNNING,
    TournamentStatus.BREAK,
    TournamentStatus.PAUSED,
    TournamentStatus.COMPLETED,
    TournamentStatus.CANCELLED,
    TournamentStatus.INITIALIZING,
  ];
  const isRestrictedState: boolean = tournamentStatus && restrictedStatuses.includes(tournamentStatus);

  // Tournament customization options
  const iconOptions = [
    { value: TournamentIcon.NONE, label: 'None' },
    { value: TournamentIcon.PHNM_LOGO, label: 'PHNM Logo' },
    { value: TournamentIcon.STAR_LOGO, label: 'Star Logo' },
  ];

  const colorOptions = [
    { value: TournamentColor.DEFAULT_WHITE, label: 'Default White' },
    { value: TournamentColor.PURPLE, label: 'Purple' },
    { value: TournamentColor.PINK, label: 'Pink' },
    { value: TournamentColor.LIGHT_BLUE, label: 'Light Blue' },
    { value: TournamentColor.LIGHT_GREEN, label: 'Light Green' },
    { value: TournamentColor.ORANGE, label: 'Orange' },
  ];

  const onSubmit = useCallback(
    (data: Partial<TournamentInfoDto>) => {
      const dirtyValues: Partial<TournamentUpdateInput> = Object.keys(dirtyFields).reduce((acc, key) => {
        acc[key] = data[key];
        return acc;
      }, {});

      if (Object.keys(dirtyFields).includes('minimumTierRequired')) {
        if (dirtyValues.minimumTierRequired === '') {
          dirtyValues.minimumTierRequired = null;
        }
      }

      onUpdate(dirtyValues);
    },
    [dirtyFields, getValues, onUpdate],
  );

  return (
    <FormProvider {...form}>
      <Modal isOpen toggle={onClose} id="edit-sng-modal" className="modal-lg" centered>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>Edit SNG Details</ModalHeader>
          <ModalBody style={{ maxHeight: '75vh' }} className="overflow-auto">
            <div className="w-100 d-flex flex-column justify-content-start max-h-[75vh] gap-2 overflow-auto">
              <div className="w-100 d-flex justify-content-between flex-row gap-2">
                <Col>
                  <Row className="w-100">
                    <Col>
                      <FormInput control={control} label="Tournament Name" name="name" disabled={isRestrictedState} />
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="game"
                        label="Game Type"
                        type="select"
                        options={Object.values(GameType).map((type) => ({ value: type, label: type }))}
                        disabled={true}
                      />
                    </Col>
                  </Row>
                  {/* Modified Buy-in and Rake Section - Always Disabled */}
                  <Row className="w-100">
                    <Col>
                      <label className="form-label">Buy-In</label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <TetherIcon width="16" height="16" />
                        </span>
                        <input
                          type="number"
                          className="form-control"
                          value={adminTotalBuyIn - adminRakeAmount}
                          disabled={true}
                          step="0.01"
                          readOnly
                        />
                      </div>
                      <small className="text-muted">Prize pool amount (cannot be modified)</small>
                    </Col>
                    <Col>
                      <label className="form-label">Rake/Fee</label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <TetherIcon width="16" height="16" />
                        </span>
                        <input
                          type="number"
                          className="form-control"
                          value={adminRakeAmount}
                          disabled={true}
                          step="0.01"
                          readOnly
                        />
                      </div>
                      <small className="text-muted">Fee amount (cannot be modified)</small>
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="maxPlayers"
                        label="Players"
                        type="number"
                        allowDecimal={false}
                        Icon={PeopleIcon}
                        disabled={true}
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="payoutStructureId"
                        label="Payout Structure"
                        type="select"
                        options={[
                          ...(payoutStructures
                            ?.filter((structure) => structure.name !== selectedTournament?.payoutStructure?.name)
                            .map((structure) => ({ value: structure.id, label: structure.name })) ?? []),
                          {
                            value: selectedTournament?.payoutStructure?.id,
                            label: selectedTournament?.payoutStructure?.name,
                          },
                        ]}
                        disabled={isRestrictedState}
                      />
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        disabled={isRestrictedState}
                        name="tableSize"
                        label="How Many Handed"
                        type="select"
                        valueAsNumber
                        options={[
                          { value: 2, label: '2 Handed' },
                          { value: 3, label: '3 Handed' },
                          { value: 4, label: '4 Handed' },
                          { value: 5, label: '5 Handed' },
                          { value: 6, label: '6 Handed' },
                          { value: 7, label: '7 Handed' },
                          { value: 8, label: '8 Handed' },
                          { value: 9, label: '9 Handed' },
                        ]}
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        disabled={isRestrictedState}
                        name="startingChips"
                        label="Starting Chips Stack"
                        type="number"
                        allowDecimal={false}
                      />
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        disabled={isRestrictedState}
                        name="blindIntervalInMinutes"
                        label="Blind Duration (mins)"
                        type="number"
                        allowDecimal={false}
                        valueAsNumber
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="blindStructureId"
                        label="Blind Structure"
                        type="select"
                        options={[
                          ...(blindStructures
                            ?.filter((structure) => structure.name !== selectedTournament?.blindStructure?.name)
                            .map((structure) => ({ value: structure.id, label: structure.name })) ?? []),
                          {
                            value: selectedTournament?.blindStructure?.id,
                            label: selectedTournament?.blindStructure?.name,
                          },
                        ]}
                        disabled={isRestrictedState}
                      />
                    </Col>
                  </Row>
                  {/* Timebank Configuration Section */}
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="startingTimebankInSeconds"
                        label="Starting Timebank (seconds)"
                        type="number"
                        allowDecimal={false}
                        valueAsNumber
                        disabled={isRestrictedState}
                        placeholder="15"
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="startingDisconnectionTimebankInSeconds"
                        label="Disconnection Timebank (seconds)"
                        type="number"
                        allowDecimal={false}
                        valueAsNumber
                        disabled={isRestrictedState}
                        placeholder="30"
                      />
                    </Col>
                  </Row>
                  <Row className="w-100">
                    <Col>
                      <FormInput
                        control={control}
                        name="timebankRefillAmountInSeconds"
                        label="Timebank Refill Amount (seconds)"
                        type="number"
                        allowDecimal={false}
                        valueAsNumber
                        disabled={isRestrictedState}
                        placeholder="30"
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="timebankRefillEveryLevels"
                        label="Refill Every X Levels (0 = disabled)"
                        type="number"
                        allowDecimal={false}
                        valueAsNumber
                        disabled={isRestrictedState}
                        placeholder="10"
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FormInput
                        control={control}
                        disabled={isRestrictedState}
                        name="minimumTierRequired"
                        label="Min Tier Required"
                        type="select"
                        options={playerTiers?.map((tier) => ({
                          value: tier.id,
                          label: tier.name,
                        }))}
                      />
                    </Col>
                    <Col />
                  </Row>

                  {/* Tournament Customization Section */}
                  <Row>
                    <Col>
                      <FormInput
                        control={control}
                        name="icon"
                        label="Tournament Icon"
                        type="select"
                        options={iconOptions}
                        disabled={selectedTournament?.status !== TournamentStatus.CREATED}
                      />
                    </Col>
                    <Col>
                      <FormInput
                        control={control}
                        name="titleColor"
                        label="Title Color"
                        type="select"
                        options={colorOptions}
                        disabled={selectedTournament?.status !== TournamentStatus.CREATED}
                      />
                    </Col>
                  </Row>
                </Col>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="grey" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!isDirty || Object.keys(errors).length > 0} type="submit" color="primary">
              {formStatus.isLoading ? 'Saving...' : 'Save'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </FormProvider>
  );
};

export default EditSngModalForm;
